<template>
  <div class="MyMapRightSideBar" :class="{ 'MyMapRightSideBar--panelUnfolded': mapStore.rightSidebarPanelUnfolded }">
    <template v-if="!mapStore.isMapExportActive">
      <SidebarSubcategoriesPanel
        v-if="mapStore.rightSidebarPanelVariant === 'osm'"
        :unfolded="mapStore.rightSidebarPanelUnfolded"
        :osm-categories="displayedCategories"
      />
      <div
        v-else-if="mapStore.rightSidebarPanelVariant === 'annotate' && mapStore.rightSidebarPanelUnfolded"
        class="MyMapRightSideBar__panelWrapper"
      >
        <MapToolsPanel @back="onMapToolsBack" />
      </div>
      <div class="MyMapRightSideBar__iconBar">
        <button
          type="button"
          class="MyMapRightSideBar__arrowBtn"
          :class="{ 'MyMapRightSideBar__arrowBtn--unfolded': mapStore.rightSidebarPanelUnfolded }"
          :title="mapStore.rightSidebarPanelUnfolded ? t('myMap.rightSidebar.foldPanel', 'Replier le panneau') : t('myMap.rightSidebar.unfoldPanel', 'Déplier le panneau')"
          @click="mapStore.rightSidebarPanelUnfolded = !mapStore.rightSidebarPanelUnfolded"
        >
          <Icon
            icon="mdi:database"
            class="MyMapRightSideBar__arrowIcon"
          />
        </button>

        <button
          v-if="displayedCategories.length > 0"
          type="button"
          class="MyMapRightSideBar__verticalLabel"
          @click="onBaseDataLabelClick"
        >
          <span class="MyMapRightSideBar__verticalLabelText">
            {{ $t('myMap.rightSidebar.baseDataLabel', 'Base de données géographiques') }}
          </span>
        </button>
      </div>
    </template>
    <MyMapExportMenu />
  </div>
</template>

<script setup lang="ts">
import {
  filterAllowedCommunes,
  getFallbackActiveCommuneTitles,
} from '@/constants/activeCommunes'
import type { AtlasCategory } from '@/models/interfaces/map/AtlasCategory'
import { CommuneService } from '@/services/commune/CommuneService'
import { AtlasCategoriesService } from '@/services/map/osm/AtlasCategoriesService'
import { fondMatchesCategory } from '@/services/map/MapStylesService'
import { useMyMapStore } from '@/stores/myMapStore'
import Icon from '@/components/ui/Icon.vue'
import SidebarSubcategoriesPanel from '@/views/map/components/geoportal/SidebarSubcategoriesPanel.vue'
import MapToolsPanel from '@/views/map/components/MapToolsPanel.vue'
import MyMapExportMenu from '@/views/map/components/export/MyMapExportMenu.vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const mapStore = useMyMapStore()
const osmCategories = ref<AtlasCategory[]>([])
const activeCommuneTitles = ref<string[]>(getFallbackActiveCommuneTitles())
const { t } = useI18n()

const displayedCategories = computed(() => {
  const allowed = new Set(mapStore.allowedOsmStyleIds)
  return osmCategories.value.filter((category) => {
    if ((category.fondLayers ?? []).length > 0) return true
    const categoryLabel = category.atlas_category ?? category.name
    if (mapStore.fondLayerStyles.some((f) => fondMatchesCategory(f, categoryLabel))) return true
    const donnees = category.donnees ?? []
    if (donnees.length === 0) return true
    if (allowed.size === 0) return true
    const hasAllowedMatch = donnees.some((donnee) => allowed.has(donnee.styleId))
    if (hasAllowedMatch) return true
    // Fallback: avoid an empty right panel when backend style IDs and map-styles IDs diverge.
    return donnees.some((donnee) => donnee.hasData !== false)
  })
})

function onBaseDataLabelClick(): void {
  if (mapStore.rightSidebarPanelUnfolded && mapStore.rightSidebarPanelVariant === 'osm') {
    mapStore.rightSidebarPanelUnfolded = false
    return
  }
  mapStore.rightSidebarPanelVariant = 'osm'
  mapStore.rightSidebarPanelUnfolded = true
}

function onMapToolsBack() {
  mapStore.rightSidebarPanelUnfolded = false
  nextTick(() => (document.activeElement as HTMLElement)?.blur())
}

/**
 * Loads the right panel tree, scoped to the locked commune when in the communal geoportail
 * (GET /api/atlas_categories?cmnCode=...): only typeOuvrages with infrastructures inside the
 * commune perimeter come back, with a commune-scoped count. Falls back to the national scope
 * outside the communal geoportail.
 */
async function loadOsmCategories(): Promise<void> {
  const categories = await AtlasCategoriesService.getAllCategories(false, mapStore.activeCommuneCode)
  osmCategories.value = categories
    .filter((category: AtlasCategory) => {
      const donnees = category.donnees ?? []
      if (donnees.length === 0) {
        return category.atlasGroup === 'Catalogue'
      }
      return donnees.some((donnee) => donnee.hasData !== false)
    })
    .sort((a, b) => a.position - b.position)
}

onMounted(async () => {
  const [, , communes] = await Promise.all([
    loadOsmCategories(),
    mapStore.fetchFondLayerStyles(),
    CommuneService.getAllCommunes().catch(() => []),
  ])

  const allowedTitles = [...new Set(filterAllowedCommunes(communes).map((commune) => commune.cmnTitre ?? '').filter(Boolean))]
  if (allowedTitles.length > 0) {
    activeCommuneTitles.value = allowedTitles
  }
})

// Re-scope the right panel tree when entering/leaving the communal geoportail or switching commune.
watch(
  () => mapStore.activeCommuneCode,
  () => {
    void loadOsmCategories()
  },
)
</script>

<style lang="scss">
.MyMapRightSideBar {
  display: flex;
  flex-flow: row nowrap;
  align-items: stretch;
  width: 4rem;
  min-width: 4rem;
  flex-shrink: 0;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
  height: 100%;
  max-height: calc(100vh - var(--mymap-header-h));
  transition: width 0.2s ease-in-out, min-width 0.2s ease-in-out;

  &--panelUnfolded {
    /* Matches the left sidebar's total expanded width (3.5rem icon rail + 24rem panel = 27.5rem). */
    width: calc(23.5rem + 4rem);
    min-width: calc(23.5rem + 4rem);
  }
}

.MyMapRightSideBar__iconBar {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  width: 4rem;
  min-width: 4rem;
  padding: 0.5rem 0;
  border-left: 1px solid transparent;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  /* Default (panel closed): eye-catching accent-colored rail. Inverted once the panel opens. */
  background: var(--color-main);
  transition: background-color 0.2s ease;
}

.MyMapRightSideBar--panelUnfolded .MyMapRightSideBar__iconBar {
  background: rgb(var(--v-theme-surface));
}

.MyMapRightSideBar__verticalLabel {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
  min-height: 0;
  margin-top: 0.7rem;
  padding: 0.6rem 0;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
  /* Soft fade at the top/bottom edges so the label blends into its block instead of cutting off abruptly. */
  -webkit-mask-image: linear-gradient(to bottom, transparent, black 18%, black 82%, transparent);
  mask-image: linear-gradient(to bottom, transparent, black 18%, black 82%, transparent);

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
}

.MyMapRightSideBar--panelUnfolded .MyMapRightSideBar__verticalLabel:hover {
  background: rgba(var(--v-theme-main-blue), 0.08);
}

.MyMapRightSideBar__verticalLabelText {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  /* Default (panel closed): white text on the accent-colored rail. */
  color: white;
  font-size: 1.15rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.3;
  transition: color 0.2s ease;
}

.MyMapRightSideBar--panelUnfolded .MyMapRightSideBar__verticalLabelText {
  color: rgba(var(--v-theme-main-blue), 0.9);
}

.MyMapRightSideBar__arrowBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  min-height: 2.5rem;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  /* Default (panel closed): white circle, accent-colored icon. */
  background-color: white;
  color: var(--color-main);
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.85);
  }

  &--unfolded .MyMapRightSideBar__arrowIcon {
    transform: scaleX(-1);
  }
}

.MyMapRightSideBar--panelUnfolded .MyMapRightSideBar__arrowBtn {
  background-color: var(--color-main);
  color: white;

  &:hover {
    background-color: var(--color-main-hover);
  }
}

.MyMapRightSideBar__arrowIcon {
  width: 1.25rem;
  height: 1.25rem;
}

.MyMapRightSideBar__panelWrapper {
  width: 23.5rem;
  min-width: 23.5rem;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  transition: min-width 0.2s ease-in-out, width 0.2s ease-in-out;
}
</style>
